export const RAREPEPE_ISSUANCE_BLOCK = 428919;

export function validSerial(serial) {
  return /^[1-9]\d{0,2}$/.test(String(serial)) && Number(serial) <= 300;
}

export function canInspect(status) {
  return status?.numbering_tip?.height >= RAREPEPE_ISSUANCE_BLOCK;
}

export function statusTitle(status) {
  if (!status) return 'Espo numbering unavailable';
  if (status.archive_error || status.projection_error) return 'Espo replay paused';
  if (status.complete && status.verified) return 'Espo numbering verified';
  if (status.complete) return 'Espo replay complete · verification pending';
  if (status.phase === 'downloading_history') return 'Espo is downloading history';
  return 'Espo is replaying history';
}

export function locationLabel(location) {
  if (!location) return 'New issuance';
  const labels = {
    address: 'Address', utxo: 'Wrapped UTXO', order: 'Order escrow',
    pending_btc_match: 'Pending BTC match', dispenser: 'Dispenser', pool: 'Pool reserve',
    fairmint_escrow: 'Fairmint escrow', fairmint_release: 'Fairmint release',
    destroyed: 'Destroyed', retired: 'Retired',
  };
  return `${labels[location.kind] || location.kind}: ${location.id}`;
}

export function createNumberingClient(transport) {
  async function call(method, params = {}) {
    const response = await transport(`xcp.${method}`, params);
    if (response?.ok !== true) throw new Error(response?.error || 'numbering_service_unavailable');
    const context = method === 'get_numbering_status' ? response : response.context;
    if (context?.network !== 'mainnet' || context.ruleset !== 'nakamoto-stack-v1') {
      throw new Error('unsupported_numbering_source');
    }
    return response;
  }
  function params(serial) {
    if (!validSerial(serial)) throw new Error('invalid_rarepepe_serial');
    return { asset: 'RAREPEPE', serial: String(serial), allow_partial: true };
  }
  return {
    status: () => call('get_numbering_status', { asset: 'RAREPEPE' }),
    unit: serial => call('get_unit', params(serial)),
    async allUnits() {
      for (let attempt = 0; attempt < 3; attempt++) {
        let context;
        let cursor;
        const items = [];
        const seenCursors = new Set();
        try {
          for (let page = 0; page < 10; page++) {
            const response = await call('get_asset_units', {
              asset: 'RAREPEPE', limit: 200, allow_partial: true,
              ...(cursor ? { cursor } : {}),
            });
            if (response.asset !== 'RAREPEPE' || !Array.isArray(response.items) || !response.context.generation) throw new Error('invalid_units_response');
            if (context && context.generation !== response.context.generation) throw new Error('stale_cursor');
            context = response.context;
            items.push(...response.items);
            if (items.length > 300) throw new Error('invalid_units_response');
            if (!response.has_more) return { context, items };
            if (!response.next_cursor || seenCursors.has(response.next_cursor)) throw new Error('invalid_units_cursor');
            cursor = response.next_cursor;
            seenCursors.add(cursor);
          }
          throw new Error('units_page_limit');
        } catch (cause) {
          if (!cause.message.includes('stale_cursor') || attempt === 2) throw cause;
        }
      }
    },
    async history(serial, previous = null) {
      const response = await call('get_unit_history', {
        ...params(serial), limit: 30,
        ...(previous ? { cursor: previous.next_cursor } : {}),
      });
      if (!Array.isArray(response.items)) throw new Error('invalid_history_response');
      if (previous && response.context.generation !== previous.context.generation) {
        throw new Error('stale_cursor');
      }
      if (response.has_more && (!response.next_cursor || response.next_cursor === previous?.next_cursor)) {
        throw new Error('invalid_history_cursor');
      }
      return response;
    },
  };
}
